import { useCallback, useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { updateProfileDetails, updateProfileLegacy } from "@peerspace/bellhop";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { useSearchParams } from "next/navigation";

import { selectExtUserId } from "~/queries/profile/profile.selectors";
import { useProfileData } from "~/queries/profile/useProfile";

import { NormalizedProfile } from "../types/profile.types";
import { createCookieOptions } from "../utils/cookie-utils";

export const useRwgToken = () => {
  const searchParams = useSearchParams();

  const isUserFAEnabled = useProfileData(selectExtUserId);
  const shouldUseNewEndpoints = Boolean(isUserFAEnabled);

  const getRWGToken = useCallback(() => {
    let rwgToken;
    try {
      const uriComponent = searchParams.get("rwg_token");
      if (uriComponent) {
        rwgToken = decodeURIComponent(uriComponent);
      }
    } catch (e) {
      datadogRum.addError(e);
    }
    return rwgToken;
  }, [searchParams]);

  const rwgToken = getRWGToken();

  // Handle rwg_token storage
  useEffect(() => {
    if (!rwgToken) return;

    // Set expiration for 30 days out
    const expDate = new Date(Date.now() + 60 * 60 * 24 * 30 * 1000);
    const cookieOptions = createCookieOptions({
      expires: expDate,
    });
    // Store rwg_token in cookies
    setCookie("rwg_val", rwgToken, cookieOptions);
    setCookie("rwg_exp", expDate, cookieOptions);
  }, [rwgToken]);

  // Function to update profile with RWG token
  const updateProfileWithRwgToken = useCallback(
    (profile: NormalizedProfile | undefined) => {
      // Check rwg token and expiration date
      const rwg = getCookie("rwg_val");
      const rwgExp = getCookie("rwg_exp");
      // `profile` is normalized (camelCase), so read the camelCased key.
      const currentRWGExp = (
        profile?.metadata as unknown as
          { gmapsBooking?: { exp?: string } } | undefined
      )?.gmapsBooking?.exp;

      if (!rwg || !rwgExp || (currentRWGExp && currentRWGExp >= rwgExp)) return;

      // We dont' need to update the user profile (state - in useProfile) since we don't use this for the user in the app
      const payload = {
        gmaps_booking: {
          rwg_token: rwg as string,
          exp: typeof rwgExp === "string" ? rwgExp.replace(/"/g, "") : rwgExp,
        },
      };
      // `gmaps_booking` isn't declared on bellhop's tightened body shapes —
      // it's a Google-specific tracking field the backend accepts but never
      // typed. Cast at this seam to keep the write.
      if (shouldUseNewEndpoints) {
        void updateProfileDetails(payload as never);
      } else {
        void updateProfileLegacy({ path: "me" }, payload as never);
      }

      deleteCookie("rwg_val");
      deleteCookie("rwg_exp");
    },
    [shouldUseNewEndpoints],
  );

  return {
    rwgToken,
    updateProfileWithRwgToken,
  };
};
